import { useMemo } from 'react'
import {
  airworthinessLight,
  aogBlock,
  holdRestrictions,
  maintenanceBlock,
  overlapsAog,
  type AircraftDefect,
  type AircraftMaintenanceEvent,
  type AirworthinessLight,
  type AogBlock,
  type DefectRestriction,
} from '@jupi/shared'
import { dateOf } from '../../components/flightLog/FlightLogCard'
import { useAircraftDefects, useMaintenanceEvents, type Booking } from '../../api/queries'

// What the calendar needs to know about an aircraft's defects: whether it is
// grounded (and for which days), and which restrictions still apply.
export interface AircraftStatus {
  light: AirworthinessLight
  aog: AogBlock | null
  // The days its open maintenance events block (a barber pole in the calendar).
  maintenance: AogBlock | null
  // Published restrictions plus those of hold items (a hold item releases
  // the aircraft but may still restrict it); never 'grounded' from a hold.
  restrictions: DefectRestriction[]
}

// Per registration, from the public defect list (published and held) and
// the open maintenance events.
export function aircraftStatuses(
  defects: AircraftDefect[],
  today = dateOf(new Date().toISOString()),
  events: AircraftMaintenanceEvent[] = [],
) {
  const byAircraft = new Map<string, AircraftDefect[]>()
  for (const defect of defects) {
    byAircraft.set(defect.registration, [...(byAircraft.get(defect.registration) ?? []), defect])
  }
  const eventsByAircraft = new Map<string, AircraftMaintenanceEvent[]>()
  for (const event of events) {
    eventsByAircraft.set(event.registration, [...(eventsByAircraft.get(event.registration) ?? []), event])
    if (!byAircraft.has(event.registration)) byAircraft.set(event.registration, [])
  }
  const statuses = new Map<string, AircraftStatus>()
  for (const [registration, list] of byAircraft) {
    const restrictions = [
      ...new Set(
        list.flatMap((d) => (d.status === 'held' ? holdRestrictions(d) : d.status === 'published' ? d.restriction : [])),
      ),
    ].filter((r) => r !== 'grounded')
    statuses.set(registration, {
      light: airworthinessLight(list),
      aog: aogBlock(list, today),
      maintenance: maintenanceBlock(eventsByAircraft.get(registration) ?? [], today),
      restrictions,
    })
  }
  return statuses
}

// Does the booking fall on AOG days? It stays active, but is shown as on hold.
export function bookingIsAog(booking: Booking, status: AircraftStatus | undefined): boolean {
  if (!status?.aog) return false
  return overlapsAog(status.aog, dateOf(booking.startsAt), dateOf(booking.endsAt))
}

export const defectsLink = (registration: string) => `/fleet/defects?registration=${registration}`

// Every aircraft's status, from one cached query shared by the calendar,
// the booking cards and the booking form.
export function useAircraftStatuses() {
  const { data } = useAircraftDefects()
  const { data: events } = useMaintenanceEvents()
  return useMemo(() => aircraftStatuses(data?.published ?? [], undefined, events ?? []), [data, events])
}

// The yellow barber pole an aircraft in maintenance carries in the calendar,
// and a maintenance booking too. Toned down: the stripes are part transparent,
// so they sit on the surface behind them instead of shouting from it, and the
// label is plain text in the theme's colour (a chip in the fleet view). The
// see-through is in the stripes' own alpha, drawn over nothing, so the grid
// and the daylight shading show through while the text stays fully opaque.
export const BARBER_POLE =
  'repeating-linear-gradient(45deg, rgba(253, 216, 53, 0.55) 0 10px, rgba(55, 71, 79, 0.42) 10px 20px)'

// How much of the AOG red is left: enough to read as the aircraft being
// grounded, not enough to dominate a week.
export const AOG_OPACITY = 0.5
